import React, { useEffect, useState } from 'react';
import { Button, InputNumber, Space, Spin, Table, Typography, message } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { getTechRates, setTechRate, formatCents, dollarsToCents, TechRate } from '../../services/moneyApi';

const { Text } = Typography;

interface EditingState {
  userId: number;
  dollarsValue: string;
  saving: boolean;
}

/**
 * Owner settings section: set cost/hr per technician.
 * Renders a table of technicians; allows inline edit of cost/hr (dollars→cents).
 * Caller must ensure owner-only rendering.
 */
const TechRateSettings: React.FC = () => {
  const [rates, setRates] = useState<TechRate[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<EditingState | null>(null);

  useEffect(() => {
    setLoading(true);
    getTechRates()
      .then(setRates)
      .catch((err) => {
        console.error('TechRateSettings fetch error:', err);
        message.error('Failed to load technician rates');
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (userId: number, dollarsStr: string) => {
    const cents = isNaN(parseFloat(dollarsStr)) ? null : dollarsToCents(dollarsStr);
    setEditing((prev) => (prev ? { ...prev, saving: true } : null));
    try {
      await setTechRate(userId, cents);
      const refreshed = await getTechRates();
      setRates(refreshed);
      setEditing(null);
    } catch (err) {
      console.error('TechRateSettings save error:', err);
      message.error('Failed to save technician rate');
      setEditing((prev) => (prev ? { ...prev, saving: false } : null));
    }
  };

  const columns = [
    {
      title: 'Technician',
      key: 'name',
      render: (_: unknown, row: TechRate) => (
        <Text>{row.firstName} {row.lastName}</Text>
      ),
    },
    {
      title: 'Current Rate',
      key: 'rate',
      width: 120,
      render: (_: unknown, row: TechRate) => (
        <Text>{row.costPerHourCents != null ? formatCents(row.costPerHourCents) + '/hr' : '—'}</Text>
      ),
    },
    {
      title: 'Set Rate ($/hr)',
      key: 'edit',
      width: 220,
      render: (_: unknown, row: TechRate) => {
        const isEditingThis = editing?.userId === row.userId;
        return (
          <Space>
            <InputNumber
              min={0}
              step={1}
              precision={2}
              placeholder="e.g. 35.00"
              value={isEditingThis ? (editing.dollarsValue as unknown as number) : undefined}
              onChange={(val) => {
                const strVal = String(val ?? '');
                setEditing({ userId: row.userId, dollarsValue: strVal, saving: false });
              }}
              style={{ width: 110 }}
            />
            {isEditingThis && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                size="small"
                loading={editing.saving}
                onClick={() => handleSave(row.userId, editing.dollarsValue)}
              >
                Save
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Spin spinning={loading}>
      <Table
        dataSource={rates}
        columns={columns}
        rowKey="userId"
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        onRow={(row) => ({ 'data-testid': `techrate-row-${row.userId}` } as any)}
        locale={{ emptyText: 'No technicians found.' }}
      />
    </Spin>
  );
};

export default TechRateSettings;
